/**
 * Account Management Page
 * 
 * Displays user subscription details and allows subscription management
 * including cancellation and resubscription without redirecting to Stripe.
 */

import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Clock,
  RefreshCw,
  Shield,
  Zap,
  ArrowLeft,
  Loader2,
  Crown,
  Star,
  Gift
} from 'lucide-react';
import CancelSubscriptionModal from '../components/CancelSubscriptionModal';
import CancelSuccessModal from '../components/CancelSuccessModal';
import type { PlanFeature } from '../types/subscription';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Fallback product names (used while loading or if API fails)
const FALLBACK_PRODUCT_NAMES: Record<string, string> = {
  'free': 'Free',
  'essential': 'Essential',
  'plus': 'Plus',
  'premium': 'Premium',
  'basic': 'Basic',
  'silver': 'Silver',
  'gold': 'Gold',
};

interface ProductInfo {
  name: string;
  displayName: string;
  planTier: string;
}

// Cache for product names fetched from API
let productNamesCache: Record<string, ProductInfo> | null = null;
let productNamesCacheTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fetch product names from Stripe API
const fetchProductNames = async (): Promise<Record<string, ProductInfo>> => {
  // Check cache first
  if (productNamesCache && Date.now() - productNamesCacheTime < CACHE_DURATION) {
    return productNamesCache;
  }
  
  try {
    const response = await fetch(`${API_BASE}/api/billing/products`);
    if (response.ok) {
      const data = await response.json();
      productNamesCache = data;
      productNamesCacheTime = Date.now();
      return data;
    }
  } catch (error) {
    console.error('Failed to fetch product names from API:', error);
  }
  
  return {};
};

// Get display name for a product ID or plan name
const getDisplayName = (productIdOrPlan: string | undefined, productNames: Record<string, ProductInfo> = {}): string => {
  if (!productIdOrPlan) return 'Free';
  
  // Check API-fetched names first
  const apiProduct = productNames[productIdOrPlan];
  if (apiProduct) return apiProduct.displayName || apiProduct.name;
  
  // Check fallback mapping
  const lowerCase = productIdOrPlan.toLowerCase();
  const fallbackMatch = FALLBACK_PRODUCT_NAMES[lowerCase];
  if (fallbackMatch) return fallbackMatch;
  
  // If it looks like a product ID but we don't recognize it, return "Premium" as fallback
  if (productIdOrPlan.startsWith('prod_')) return 'Premium';
  
  // Capitalize first letter as fallback
  return productIdOrPlan.charAt(0).toUpperCase() + productIdOrPlan.slice(1);
};

interface SubscriptionDetails {
  plan: string;
  status: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  cancelledAt?: string;
  subscriptionExpiresAt?: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  productId?: string;
  isTrialing?: boolean;
  trialEnd?: string;
}

const planFeatures: Record<string, PlanFeature[]> = {
  basic: [
    { name: 'Real-time market data', included: true },
    { name: 'Basic charting tools', included: true },
    { name: 'Email support', included: true },
  ],
  silver: [
    { name: 'Everything in Basic', included: true },
    { name: 'Advanced charting', included: true },
    { name: 'Trade replay feature', included: true },
    { name: 'Priority support', included: true },
  ],
  gold: [
    { name: 'Everything in Silver', included: true },
    { name: 'AI-powered insights', included: true },
    { name: 'Unlimited replay sessions', included: true },
    { name: 'Premium indicators', included: true },
    { name: '24/7 priority support', included: true },
  ],
  premium: [
    { name: 'Everything in Gold', included: true },
    { name: 'Custom indicators', included: true },
    { name: 'API access', included: true },
    { name: 'Dedicated account manager', included: true },
  ],
};

const AccountManagement: React.FC = () => {
  const { user, isLoaded: userLoaded } = useUser();
  const { getToken } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelSuccessOpen, setIsCancelSuccessOpen] = useState(false);
  const [cancelledPlanName, setCancelledPlanName] = useState('');
  const [expiresAt, setExpiresAt] = useState<number | undefined>(undefined);
  const [productNames, setProductNames] = useState<Record<string, ProductInfo>>({});

  // Fetch product names from Stripe API on mount
  useEffect(() => {
    fetchProductNames().then(setProductNames);
  }, []);

  useEffect(() => {
    if (userLoaded && user) {
      fetchSubscriptionDetails();
    }
  }, [userLoaded, user]);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      const response = await fetch(`${API_BASE}/api/billing/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription details');
      }

      const data = await response.json();
      
      // Also get metadata from user
      const metadata = user?.publicMetadata || {};
      
      setSubscription({
        plan: metadata.subscriptionPlan as string || 'free',
        status: metadata.subscriptionStatus as string || 'inactive',
        currentPeriodEnd: metadata.currentPeriodEnd as string,
        cancelAtPeriodEnd: metadata.cancelAtPeriodEnd as boolean,
        cancelledAt: metadata.cancelledAt as string,
        subscriptionExpiresAt: metadata.subscriptionExpiresAt as string,
        stripeSubscriptionId: metadata.stripeSubscriptionId as string,
        stripeCustomerId: metadata.stripeCustomerId as string,
        productId: metadata.productId as string,
        isTrialing: metadata.isTrialing as boolean,
        trialEnd: metadata.trialEnd as string,
        ...data.subscription,
      });
    } catch (err) {
      console.error('Error fetching subscription:', err);
      // Fallback to user metadata
      const metadata = user?.publicMetadata || {};
      setSubscription({
        plan: metadata.subscriptionPlan as string || 'free',
        status: metadata.subscriptionStatus as string || 'inactive',
        currentPeriodEnd: metadata.currentPeriodEnd as string,
        cancelAtPeriodEnd: metadata.cancelAtPeriodEnd as boolean,
        cancelledAt: metadata.cancelledAt as string,
        subscriptionExpiresAt: metadata.subscriptionExpiresAt as string,
        stripeSubscriptionId: metadata.stripeSubscriptionId as string,
        stripeCustomerId: metadata.stripeCustomerId as string,
        productId: metadata.productId as string,
        isTrialing: metadata.isTrialing as boolean,
        trialEnd: metadata.trialEnd as string,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      const response = await fetch(`${API_BASE}/api/billing/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: 'User requested cancellation',
          feedback: '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel subscription');
      }

      const data = await response.json();
      
      setCancelledPlanName(getDisplayName(subscription?.productId || subscription?.plan, productNames) || 'Premium');
      // Convert ISO date string to timestamp for CancelSuccessModal
      const expiresAtDate = data.details?.remainsActiveUntil;
      setExpiresAt(expiresAtDate ? new Date(expiresAtDate).getTime() : undefined);
      setIsCancelModalOpen(false);
      setIsCancelSuccessOpen(true);
      
      // Refresh subscription details
      await fetchSubscriptionDetails();
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResubscribe = async () => {
    try {
      setActionLoading(true);
      setError(null);
      
      const token = await getToken();
      
      const response = await fetch(`${API_BASE}/api/billing/resubscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate subscription');
      }

      // Refresh subscription details
      await fetchSubscriptionDetails();
    } catch (err: any) {
      console.error('Error resubscribing:', err);
      setError(err.message || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'trialing':
        return 'text-emerald-400 bg-emerald-500/20';
      case 'cancelled':
      case 'canceled':
        return 'text-red-400 bg-red-500/20';
      case 'past_due':
        return 'text-yellow-400 bg-yellow-500/20';
      default:
        return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'trialing':
        return <CheckCircle className="w-5 h-5" />;
      case 'cancelled':
      case 'canceled':
        return <XCircle className="w-5 h-5" />;
      case 'past_due':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const getPlanIcon = (plan: string) => {
    const displayName = getDisplayName(plan, productNames).toLowerCase();
    switch (displayName) {
      case 'gold':
      case 'premium':
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 'silver':
      case 'plus':
      case 'essential':
        return <Star className="w-6 h-6 text-slate-300" />;
      default:
        return <Zap className="w-6 h-6 text-blue-400" />;
    }
  };

  const getPlanGradient = (plan: string) => {
    const displayName = getDisplayName(plan, productNames).toLowerCase();
    switch (displayName) {
      case 'gold':
      case 'premium':
        return 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
      case 'silver':
      case 'plus':
      case 'essential':
        return 'from-slate-400/20 to-slate-500/20 border-slate-400/30';
      default:
        return 'from-blue-500/20 to-indigo-500/20 border-blue-500/30';
    }
  };

  if (!userLoaded || loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <p className="text-slate-400">Loading account details...</p>
        </div>
      </div>
    );
  }

  const isFreePlan = !subscription?.plan || subscription.plan === 'free';
  const hasActiveSubscription = subscription?.stripeSubscriptionId && subscription.status !== 'cancelled';
  const isCancelling = subscription?.cancelAtPeriodEnd;
  
  // Get the display name for the plan (converts product IDs to readable names from Stripe API)
  const planDisplayName = getDisplayName(subscription?.productId || subscription?.plan, productNames);
  const features = planFeatures[planDisplayName.toLowerCase()] || planFeatures.basic;

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-white">Account Management</h1>
          <p className="text-slate-400 mt-2">Manage your subscription and billing details</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Subscription Card */}
        <div className={`bg-gradient-to-br ${getPlanGradient(subscription?.plan || 'free')} border rounded-2xl p-6 mb-6`}>
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-slate-800/50 rounded-xl flex items-center justify-center">
                {getPlanIcon(subscription?.plan || 'free')}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {planDisplayName} Plan
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription?.status || 'inactive')}`}>
                    {getStatusIcon(subscription?.status || 'inactive')}
                    {subscription?.isTrialing ? 'Trial' : (subscription?.status || 'Inactive')}
                  </span>
                  {isCancelling && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium text-orange-400 bg-orange-500/20">
                      <Clock className="w-4 h-4" />
                      Cancels Soon
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={fetchSubscriptionDetails}
              disabled={loading}
              className="p-2 text-slate-400 hover:text-white transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Subscription Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {subscription?.isTrialing && subscription?.trialEnd && (
              <div className="bg-slate-800/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <Gift className="w-4 h-4" />
                  Trial Ends
                </div>
                <p className="text-white font-semibold">{formatDate(subscription.trialEnd)}</p>
              </div>
            )}
            
            {!isFreePlan && (
              <>
                <div className="bg-slate-800/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    {isCancelling ? 'Access Until' : 'Next Billing Date'}
                  </div>
                  <p className="text-white font-semibold">
                    {formatDate(subscription?.subscriptionExpiresAt || subscription?.currentPeriodEnd)}
                  </p>
                </div>

                <div className="bg-slate-800/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                    <CreditCard className="w-4 h-4" />
                    Billing Status
                  </div>
                  <p className="text-white font-semibold">
                    {isCancelling ? 'Will not renew' : 'Auto-renew enabled'}
                  </p>
                </div>
              </>
            )}

            {subscription?.stripeCustomerId && (
              <div className="bg-slate-800/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
                  <Shield className="w-4 h-4" />
                  Customer ID
                </div>
                <p className="text-white font-mono text-sm truncate">
                  {subscription.stripeCustomerId}
                </p>
              </div>
            )}
          </div>

          {/* Cancellation Warning */}
          {isCancelling && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-orange-400 font-medium">Subscription Cancellation Scheduled</p>
                  <p className="text-orange-300 text-sm mt-1">
                    Your subscription will end on {formatDate(subscription?.subscriptionExpiresAt || subscription?.currentPeriodEnd)}. 
                    You'll continue to have access to all features until then.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {hasActiveSubscription && !isCancelling && (
              <button
                onClick={() => setIsCancelModalOpen(true)}
                disabled={actionLoading}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Cancel Subscription
              </button>
            )}

            {isCancelling && (
              <button
                onClick={handleResubscribe}
                disabled={actionLoading}
                className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Reactivate Subscription
              </button>
            )}

            {isFreePlan && (
              <a
                href="/pricing"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-medium transition-all flex items-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Upgrade Now
              </a>
            )}
          </div>
        </div>

        {/* Plan Features */}
        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            Your Plan Features
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-3 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                {typeof feature === 'string' ? feature : feature.name}
              </li>
            ))}
          </ul>
        </div>

        {/* Account Info */}
        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Account Information</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-700">
              <span className="text-slate-400">Email</span>
              <span className="text-white">{user?.primaryEmailAddress?.emailAddress || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-700">
              <span className="text-slate-400">Name</span>
              <span className="text-white">{user?.fullName || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-700">
              <span className="text-slate-400">Member Since</span>
              <span className="text-white">{formatDate(user?.createdAt?.toString())}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-slate-400">User ID</span>
              <span className="text-white font-mono text-sm">{user?.id || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 text-center">
          <p className="text-slate-400 text-sm">
            Need help with your subscription?{' '}
            <a href="mailto:support@traderewind.com" className="text-blue-400 hover:text-blue-300 underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirmCancel={handleCancelSubscription}
        currentPlan={planDisplayName}
        planFeatures={features}
      />

      {/* Cancel Success Modal */}
      <CancelSuccessModal
        isOpen={isCancelSuccessOpen}
        onClose={() => setIsCancelSuccessOpen(false)}
        previousPlan={cancelledPlanName}
        subscriptionEndDate={expiresAt}
      />
    </div>
  );
};

export default AccountManagement;
